import React, { useEffect, useContext } from 'react';
import { StyleSheet, View, TouchableOpacity, Text } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useSelector, useDispatch } from 'react-redux';
import AppSwitch from 'components/AppSwitch';

import { joinRoom } from 'store/communication/actions/controlActions';
import { goToMeeting, setCustomizerClosing } from 'store/app';
import { setLocation } from 'store/world/actions';
import VideoContainer from 'components/Communication/VideoContainer';
import AudioMonitor from 'components/Configs/AudioMonitor';
import {
  enableMic,
  disableMic,
  disableCamera,
  enableCamera,
  switchCamera,
} from 'store/media';
import UniversalButton from 'components/UniversalButton';
import SettingsDialogueOverlay from 'components/Communication/SettingsDialogueOverlay';
import { MICROPHONE_VOLUME_INDICATOR } from 'style/theme';
import { I18nContext } from 'i18n/context/I18nContext';

const DeviceConfig = () => {
  const dispatch = useDispatch();
  const { translate } = useContext(I18nContext);
  const {
    micEnabled,
    cameraEnabled,
    canSwitchCamera,
    camStream,
    micStream,
    mirrorCamera,
  } = useSelector((state) => state.media);

  useEffect(() => {
    if (!micEnabled) {
      console.log('[MP_RN] -> enableMic: from DeviceConfig useEffect (mount, micEnabled was false)');
      dispatch(enableMic());
    }
    // Explicit hide for the customizer→DeviceConfig transition overlay.
    // The reducer also resets this on GO_TO_DEVICE_CONFIG as a safety net,
    // but dispatching here keeps the signal traceable from the mount site
    // and is a no-op on native (the flag is never set there).
    dispatch(setCustomizerClosing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onJoin = async () => {
    console.log('DeviceConfig onJoin pressed');

    // Local fallback: set the world location to 'house' so MeetingWorld
    // can load the embedded house bundle even if protoo room configs
    // never arrive (e.g. server unreachable). When the server does send
    // roomConfigs with a startingLocation, RoomClient.setLocalRoomConfigs
    // will overwrite this value for us.
    dispatch(setLocation('house'));

    try {
      await dispatch(joinRoom());
      dispatch(goToMeeting());
    } catch (error) {
      console.error('DeviceConfig joinRoom error', error);
      // Still navigate to meeting so the user gets the local house
      // experience when the server is unreachable. Multiplayer features
      // (peers, voice, viewboards) won't work, but the scene loads.
      dispatch(goToMeeting());
    }
  };

  const changeCamera = async () => {
    dispatch(switchCamera());
  };

  const toggleMic = () => {
    console.log('MIC', micEnabled)
    if (micEnabled) {
      console.log('[MP_RN] -> disableMic: from DeviceConfig toggleMic (user tap)');
      dispatch(disableMic());
    } else {
      console.log('[MP_RN] -> enableMic: from DeviceConfig toggleMic (user tap)');
      dispatch(enableMic());
    }
  };

  const toggleCamera = async () => {
    if (cameraEnabled) {
      await dispatch(disableCamera());
    } else {
      await dispatch(enableCamera());
    }
  };

  const micIcon = micEnabled ? 'microphone-outline' : 'microphone-off';
  const cameraIcon = cameraEnabled ? 'video-outline' : 'video-off-outline';

  const micVolumeIconStyle = micEnabled
    ? styles.micEnabledIcon
    : styles.micDisabledIcon;

  const allowChangeCamera = cameraEnabled && canSwitchCamera;
  const cameraSwitchIconColor = allowChangeCamera ? '#000' : '#CCC';
  const cameraSwitchButtonColor = allowChangeCamera ? '#FFF' : '#FFF';

  return (
    <View style={styles.container}>
      <View style={[styles.videoContainer]} pointerEvents="none">
        {cameraEnabled ? (
          <VideoContainer
            style={styles.videoPane}
            stream={camStream}
            pointerEvents="box-none"
            waiting={false}
            mirror={mirrorCamera}
          />
        ) : (
          <View style={[styles.videoPane, styles.emptyVideo]}>
            <Icon style={[styles.emptyVideoIcon]} name={'video-off'} />
          </View>
        )}
      </View>
      <View style={styles.volumeTray}>
        <Icon style={micVolumeIconStyle} name="microphone-outline" />
        <AudioMonitor style={MICROPHONE_VOLUME_INDICATOR} stream={micStream} />
      </View>
      <View style={styles.controlTray}>
        <View style={styles.iconSwitch}>
          <Icon style={[styles.icon]} name={micIcon} />
          <AppSwitch onValueChange={toggleMic} value={micEnabled} />
        </View>
        <View style={styles.iconSwitch}>
          <Icon style={[styles.icon]} name={cameraIcon} />
          <AppSwitch onValueChange={() => toggleCamera()} value={cameraEnabled} />
        </View>
        <UniversalButton
          name="video-switch"
          onPress={changeCamera}
          color={cameraSwitchButtonColor}
          iconColor={cameraSwitchIconColor}
          style={styles.switchButton}
        />
      </View>
      <View style={styles.joinButtonContainer}>
        <TouchableOpacity
          style={styles.joinButton}
          onPress={onJoin}
          activeOpacity={0.8}
        >
          <Text style={styles.joinButtonText}>
            {translate('deviceConfig.join')}
          </Text>
        </TouchableOpacity>
      </View>
      {/* <SettingsDialogueOverlay /> */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFF',
  },
  icon: {
    color: '#000',
    fontSize: 40,
    textAlign: 'left',
    height: 40,
  },
  videoContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoPane: {
    width: 300,
    height: 300,
  },
  emptyVideo: {
    justifyContent: 'center',
    alignItems: 'center',
    borderColor: '#CCC',
    borderWidth: 3,
    borderRadius: 16,
  },
  emptyVideoIcon: {
    color: '#CCC',
    fontSize: 96,
    textAlign: 'center',
    height: 96,
  },
  controlTray: {
    height: 96,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
  },
  iconSwitch: {
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    borderColor: '#000',
    borderWidth: 1,
    borderRadius: 24,
    paddingHorizontal: 12,
    gap: 12,
  },
  switchButton: {
    borderWidth: 1,
    borderColor: '#000',
  },
  micEnabledIcon: {
    color: '#000',
    fontSize: 40,
    textAlign: 'left',
    height: 40,
  },
  micDisabledIcon: {
    color: '#CCC',
    fontSize: 40,
    textAlign: 'left',
    height: 40,
  },
  volumeTray: {
    width: 300,
    height: 48,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  joinButtonContainer: {
    marginTop: 32,
    zIndex: 10,
    elevation: 10
  },
  joinButton: {
    width: 260,
    borderRadius: 24,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#FF4500',
  },
  joinButtonText: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: '600',
  },
});

export default DeviceConfig;
